/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable camelcase */

import React from 'react';
import { formatDistance, unpackToDateTime } from '@campfire/hot-date';
import { HoverText } from '@campfire/hover-link';
import { TabletButton } from '@campfire/tablet-button';
import { Box, CircularProgress, IconButton, MenuItem, Switch, TextField, Typography } from '@material-ui/core';
import {
  Add as AddIcon,
  CheckCircle,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';
import { useCommonStyles, useSelectStyles } from '../../commonStyles';

const useStyles = makeStyles(() =>
  createStyles({
    sectionTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '0.25rem',
      marginBottom: '1.5rem',
    },
    switchLabel: {
      fontWeight: 500,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
    },
    switchDescription: {
      fontSize: '13px',
      color: '#808080',
    },
    statusTitle: {
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      letterSpacing: '0.008em',
    },
    statusSubtitle: {
      fontWeight: 400,
      fontSize: '0.813rem',
      lineHeight: '1.125rem',
      letterSpacing: '0.008em',
      opacity: '60%',
    },
    statusCaption: {
      fontWeight: 400,
      fontStyle: 'italic',
      fontSize: '0.75rem',
      lineHeight: '1.375rem',
      letterSpacing: '0.008em',
      opacity: '60%',
    },
    authorisationText: {
      fontSize: '14px',
      fontWeight: 400,
      opacity: '60%',
      lineHeight: '18px',
      letterSpacing: '0.2px',
    },
  })
);

type ISalesforceAccount = {
  Id: string;
  Name: string;
};

type ISalesforceManageData = {
  allAccounts: Array<ISalesforceAccount>;
  selectedAccountId: string | null;
  instanceUrl?: string;
  dateCreated?: string;
};

type IFieldMapping = {
  id?: string;
  volabyField: string;
  salesforceField: string;
};

type ISalesforceConfigData = {
  addVolunteers: boolean;
  removeVolunteers: boolean;
  programSegments: boolean;
  syncPhone: boolean;
  syncDateOfBirth: boolean;
  syncPreferredName: boolean;
  syncAboutMe: boolean;
  fieldMappings: IFieldMapping[];
};

type ISalesforceField = {
  name: string;
  label: string;
  type: string;
  isCustom: boolean;
};

type ISalesforceFieldsData = {
  fields: ISalesforceField[];
};

const VOLABY_FIELDS = [
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'preferredName', label: 'Preferred Name' },
];

type SwitchItemProps = {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
};

function SwitchItem({ checked, onChange, label, description }: SwitchItemProps) {
  const classes = useStyles();

  return (
    <Box mt={2}>
      <Box display='flex' alignItems='center'>
        <Switch checked={checked} onChange={onChange} color='primary' />
        <Box ml={1}>
          <Typography className={classes.switchLabel}>{label}</Typography>
          <Typography variant='body2' className={classes.switchDescription}>
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function SalesforceSetups() {
  const classes = useStyles();
  const inputClasses = useSelectStyles();
  const commonStyles = useCommonStyles();
  const { theme, isSm } = useCampfireTheme();
  const apiUrl = useApiUrl();

  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);

  // Manage data (account selection)
  const manageFetch = useCampfireFetch<ISalesforceManageData>({ defer: true });
  const saveManageFetch = useCampfireFetch({ defer: true });
  const [manageData, setManageData] = React.useState<ISalesforceManageData | null>(null);
  const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null);
  const [initialAccountId, setInitialAccountId] = React.useState<string | null>(null);

  // Configuration data (sync settings)
  const configFetch = useCampfireFetch<ISalesforceConfigData>({ defer: true });
  const saveConfigFetch = useCampfireFetch({ defer: true });
  const [config, setConfig] = React.useState<ISalesforceConfigData>({
    addVolunteers: false,
    removeVolunteers: false,
    programSegments: false,
    syncPhone: false,
    syncDateOfBirth: false,
    syncPreferredName: false,
    syncAboutMe: false,
    fieldMappings: [],
  });
  // Salesforce fields for mapping
  const fieldsFetch = useCampfireFetch<ISalesforceFieldsData>({ defer: true });
  const [salesforceFields, setSalesforceFields] = React.useState<ISalesforceField[]>([]);

  // Connection info
  const [instanceUrl, setInstanceUrl] = React.useState<string | null>(null);
  const [dateCreated, setDateCreated] = React.useState<string | null>(null);

  React.useEffect(() => {
    Promise.all([
      manageFetch.run({
        url: `${apiUrl}/wm/integrations/salesforce/manage/get`,
        method: 'get',
      }),
      configFetch.run({
        url: `${apiUrl}/wm/integrations/salesforce/configuration/get`,
        method: 'get',
      }),
    ])
      .then(([manageResponse, configResponse]) => {
        const manageResponseData = manageResponse.data.data;
        setManageData(manageResponseData);
        setSelectedAccountId(manageResponseData.selectedAccountId);
        setInitialAccountId(manageResponseData.selectedAccountId);
        setInstanceUrl(manageResponseData.instanceUrl || null);
        setDateCreated(manageResponseData.dateCreated || null);

        const configData = configResponse.data.data;
        setConfig(configData);

        // Fetch Salesforce fields if connected
        if (manageResponseData.selectedAccountId) {
          return fieldsFetch.run({
            url: `${apiUrl}/wm/integrations/salesforce/fields/get`,
            method: 'get',
          });
        }
        return null;
      })
      .then((fieldsResponse) => {
        if (fieldsResponse?.data?.data?.fields) {
          setSalesforceFields(fieldsResponse.data.data.fields);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onSave = async () => {
    setIsSaving(true);

    try {
      // Save account if changed
      if (selectedAccountId !== initialAccountId && selectedAccountId) {
        await saveManageFetch.run({
          url: `${apiUrl}/wm/integrations/salesforce/manage/save`,
          method: 'post',
          data: { accountId: selectedAccountId },
        });
        setInitialAccountId(selectedAccountId);
      }

      // Save configuration
      await saveConfigFetch.run({
        url: `${apiUrl}/wm/integrations/salesforce/configuration/save`,
        method: 'post',
        data: config,
      });
      setEditMode(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigChange = (field: keyof ISalesforceConfigData) => () => {
    setConfig((prev) => ({
      ...prev,
      [field]: !prev[field as keyof Omit<ISalesforceConfigData, 'fieldMappings'>],
    }));
  };

  const addFieldMapping = () => {
    setConfig((prev) => ({
      ...prev,
      fieldMappings: [...prev.fieldMappings, { id: `mapping-${Date.now()}`, volabyField: '', salesforceField: '' }],
    }));
  };

  const removeFieldMapping = (index: number) => {
    setConfig((prev) => ({
      ...prev,
      fieldMappings: prev.fieldMappings.filter((_unused, i) => i !== index),
    }));
  };

  const updateFieldMapping = (index: number, field: 'volabyField' | 'salesforceField', value: string) => {
    setConfig((prev) => ({
      ...prev,
      fieldMappings: prev.fieldMappings.map((mapping, i) => (i === index ? { ...mapping, [field]: value } : mapping)),
    }));
  };

  // Get the selected account name for display
  const selectedAccountName = manageData?.allAccounts?.find((a) => a.Id === selectedAccountId)?.Name;

  // Format last updated date
  const getLastUpdatedText = () => {
    if (dateCreated) {
      try {
        return `Last updated ${formatDistance(unpackToDateTime(dateCreated)).toLowerCase()}`;
      } catch {
        return 'Last updated today';
      }
    }
    return 'Last updated today';
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {/* Your Account Section */}
      <Box className={commonStyles.containerPanel}>
        <Typography variant='h2' className={commonStyles.headingTitle}>
          Your Account
        </Typography>
        <Box display='flex' flexDirection={isSm ? 'column' : 'row'} py='1rem'>
          <Box flex={1} borderRight={isSm ? 'none' : '1px solid #dfdfdf'} mr='1rem'>
            <Typography className={commonStyles.sectionHeading}>Status</Typography>
            <Box display='flex' alignItems='flex-start' justifyContent='space-between' pt='1rem' pr='1rem'>
              <Box display='flex' alignItems='flex-start'>
                <CheckCircle fontSize='small' style={{ color: theme.alert.green.light }} />
                <Box pl='0.5rem'>
                  <Typography className={classes.statusTitle}>Active & Logged In</Typography>
                  <Typography className={classes.statusSubtitle}>
                    {instanceUrl || selectedAccountName || 'Connected to Salesforce'}
                  </Typography>
                  <Typography className={classes.statusCaption}>{getLastUpdatedText()}</Typography>
                </Box>
              </Box>
              <TabletButton
                variant='contained'
                size='small'
                style={{
                  backgroundColor: '#A0AEC0',
                  color: '#ffffff',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  fontWeight: 600,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                }}
              >
                Sign Out
              </TabletButton>
            </Box>
          </Box>
          <Box flex={1}>
            <Typography className={commonStyles.sectionHeading}>Authorisations</Typography>
            <Box pt='0.5rem' className={classes.authorisationText}>
              <p>Volaby is authorised to:</p>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                <li>View and manage your Salesforce contacts</li>
                <li>Create and update contact records</li>
                <li>Access account information</li>
              </ul>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Account Selection */}
      <Box className={commonStyles.containerPanel}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box>
            <Typography className={classes.sectionTitle}>
              <SettingsIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
              Salesforce Account
            </Typography>
            <Typography variant='body2' className={classes.sectionDescription}>
              Select which Salesforce account to sync volunteer data with
            </Typography>
          </Box>
          <HoverText
            color={editMode ? 'error' : 'textSecondary'}
            variant='body2'
            invertUnderlineBehaviour
            onClick={() => setEditMode(!editMode)}
            style={{
              fontSize: '12px',
            }}
          >
            {editMode ? 'Cancel' : 'Edit'}
          </HoverText>
        </Box>

        <Box width={300}>
          <TextField
            select
            fullWidth
            variant='outlined'
            value={selectedAccountId || ''}
            onChange={(event) => setSelectedAccountId(event.target.value)}
            InputProps={{ classes: inputClasses }}
            disabled={!editMode}
          >
            {(manageData?.allAccounts || []).map((account) => (
              <MenuItem key={account.Id} value={account.Id}>
                {account.Name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Sync Options */}
      <Box className={commonStyles.containerPanel}>
        <Typography className={classes.sectionTitle}>
          <SyncIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Contact Sync
        </Typography>
        <Typography variant='body2' className={classes.sectionDescription}>
          Configure how volunteer data is synced to Salesforce contacts
        </Typography>

        <SwitchItem
          checked={config.addVolunteers}
          onChange={handleConfigChange('addVolunteers')}
          label='Add & Update Volunteers'
          description='New volunteers will be added as contacts and updated when their details change in Volaby.'
        />
        <SwitchItem
          checked={config.removeVolunteers}
          onChange={handleConfigChange('removeVolunteers')}
          label='Remove Volunteers'
          description='Retired volunteers will be removed as contacts from Salesforce.'
        />
        <SwitchItem
          checked={config.programSegments}
          onChange={handleConfigChange('programSegments')}
          label='Program Segments'
          description='Contacts will be segmented by program. Program membership changes will be reflected in Salesforce.'
        />
      </Box>

      {/* Optional Fields */}
      <Box className={commonStyles.containerPanel}>
        <Typography className={classes.sectionTitle}>
          <SyncIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Optional Fields
        </Typography>
        <Typography variant='body2' className={classes.sectionDescription}>
          First Name, Last Name, and Email are always synced. Enable additional fields below.
        </Typography>

        <SwitchItem
          checked={config.syncPhone}
          onChange={handleConfigChange('syncPhone')}
          label='Phone'
          description="Sync the volunteer's contact number to Salesforce."
        />
        <SwitchItem
          checked={config.syncDateOfBirth}
          onChange={handleConfigChange('syncDateOfBirth')}
          label='Date of Birth'
          description="Sync the volunteer's date of birth to Salesforce."
        />
        <SwitchItem
          checked={config.syncPreferredName}
          onChange={handleConfigChange('syncPreferredName')}
          label='Preferred Name'
          description="Sync the volunteer's preferred name to a custom Salesforce field."
        />
        <SwitchItem
          checked={config.syncAboutMe}
          onChange={handleConfigChange('syncAboutMe')}
          label='About Me'
          description="Sync the volunteer's about me text to Salesforce."
        />
      </Box>

      {/* Custom Field Mappings */}
      <Box className={commonStyles.containerPanel}>
        <Typography className={classes.sectionTitle}>
          <SyncIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Custom Field Mappings
        </Typography>
        <Typography variant='body2' className={classes.sectionDescription}>
          Map Volaby fields to custom Salesforce fields for advanced integrations.
        </Typography>

        {config.fieldMappings.map((mapping, index) => (
          <Box
            key={mapping.id || `mapping-legacy-${mapping.volabyField}-${mapping.salesforceField}`}
            display='flex'
            alignItems='center'
            mb='0.75rem'
            style={{ gap: '0.75rem' }}
          >
            <TextField
              select
              variant='outlined'
              value={mapping.volabyField}
              onChange={(e) => updateFieldMapping(index, 'volabyField', e.target.value)}
              InputProps={{ classes: inputClasses }}
              style={{ width: 180 }}
              size='small'
            >
              <MenuItem value=''>Select field...</MenuItem>
              {VOLABY_FIELDS.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <Typography style={{ color: '#9E9E9E' }}>→</Typography>

            <TextField
              select
              variant='outlined'
              value={mapping.salesforceField}
              onChange={(e) => updateFieldMapping(index, 'salesforceField', e.target.value)}
              InputProps={{ classes: inputClasses }}
              style={{ width: 220 }}
              size='small'
            >
              <MenuItem value=''>Select field...</MenuItem>
              {salesforceFields.map((field) => (
                <MenuItem key={field.name} value={field.name}>
                  {field.label}
                  {field.isCustom && ' (Custom)'}
                </MenuItem>
              ))}
            </TextField>

            <IconButton size='small' onClick={() => removeFieldMapping(index)}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        ))}

        <Box mt='1rem'>
          <HoverText
            color='primary'
            variant='body2'
            onClick={addFieldMapping}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '13px',
              cursor: salesforceFields.length === 0 ? 'not-allowed' : 'pointer',
              opacity: salesforceFields.length === 0 ? 0.5 : 1,
            }}
          >
            <AddIcon fontSize='small' style={{ marginRight: '4px' }} />
            Add Field Mapping
          </HoverText>
        </Box>
      </Box>

      {/* Save Button */}
      {editMode && (
        <Box display='flex' justifyContent='flex-end' mt='1rem' mr='1.875rem'>
          <TabletButton
            onClick={onSave}
            color='primary'
            variant='contained'
            disabled={isSaving}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              padding: '7px 12px',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            {isSaving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Save'}
          </TabletButton>
        </Box>
      )}
    </Box>
  );
}
